import { Capacitor } from '@capacitor/core';
import { CapacitorCalendar, CalendarPermissionScope } from '@ebarooni/capacitor-calendar';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { AppLauncher } from '@capacitor/app-launcher';
import moment from 'moment-timezone';

let listener = null;

async function savePendingCalendarEvent(event) {
  await Preferences.set({
    key: 'pendingCalendarEvent',
    value: JSON.stringify(event)
  });
}

async function getPendingCalendarEvent() {
  const { value } = await Preferences.get({ key: 'pendingCalendarEvent' });
  return value ? JSON.parse(value) : null;
}

async function clearPendingCalendarEvent() {
  await Preferences.remove({ key: 'pendingCalendarEvent' });
}

async function addAppStateListener(callback) {
  async function handleAppStateChange(state) {
    if (state.isActive) {
      if (listener) {
        listener.remove();
        listener = null;
      }

      let permission = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.WRITE_CALENDAR });
      if (permission.result === 'granted') {
        await callback();
      }
    }
  }

  if (listener) {
    listener.remove();
    listener = null;
  }
  listener = await App.addListener('appStateChange', handleAppStateChange);
}

async function checkAndRecoverCalendarEvent() {
  const event = await getPendingCalendarEvent();
  if (event) {
    let permission = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.WRITE_CALENDAR });
    if (permission.result === 'granted') {
      await CapacitorCalendar.createEventWithPrompt(event);
      await clearPendingCalendarEvent();
    }
  }
}

async function addEventToCalendarPromptHelper(event) {
  if (Capacitor.getPlatform() === 'ios') {
    await CapacitorCalendar.requestWriteOnlyCalendarAccess();
  } else {
    await CapacitorCalendar.requestFullCalendarAccess();
  }

  let permission = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.WRITE_CALENDAR });
  if (permission.result !== 'granted') {
    if (Capacitor.getPlatform() === 'ios') {
      await savePendingCalendarEvent(event);
      await addAppStateListener(async () => {
        await checkAndRecoverCalendarEvent();
      });
    }

    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App
    });

    if (Capacitor.getPlatform() === 'android') {
      permission = await CapacitorCalendar.checkPermission({ scope: CalendarPermissionScope.WRITE_CALENDAR });
      if (permission.result === 'granted') {
        await CapacitorCalendar.createEventWithPrompt(event);
        return true;
      }
    }
    return false;
  }

  await CapacitorCalendar.createEventWithPrompt(event);
  return true;
}

async function addEventToCalendarPrompt(title, start, end, isAllDay, location, details) {
  let result = false;
  let formatDateForGoogle = date => moment(date).format('YYYYMMDD');

  // Truncate based on byte length to handle multi-byte characters (e.g., Chinese)
  // URL encoding can expand bytes further, so we use a conservative limit
  const truncateByBytes = (str, maxBytes) => {
    if (!str) return str;
    const encoder = new TextEncoder();
    let bytes = encoder.encode(str);
    if (bytes.length <= maxBytes) return str;

    // Keep the last maxBytes, but ensure we don't cut in the middle of a character
    const decoder = new TextDecoder('utf-8', { fatal: false });
    let truncated = decoder.decode(bytes.slice(-maxBytes));
    // Remove any leading invalid character fragments
    truncated = truncated.replace(/^[^\s\S]*?[\uFFFD]/, '');
    return truncated;
  };

  const truncatedDetails = truncateByBytes(details, 5000); // Conservative limit for URL encoding

  if (Capacitor.isNativePlatform()) {
    let canOpenGoogleCalendar = false;
    if (Capacitor.getPlatform() === 'ios') {
      let checkResult = await AppLauncher.canOpenUrl({ url: 'com.google.calendar://event?action=create' });
      canOpenGoogleCalendar = checkResult.value;
      var { value: defaultCalendar } = await Preferences.get({ key: 'defaultCalendar' });
    }

    if (canOpenGoogleCalendar && defaultCalendar === 'google-calendar') {
      let googleCalendarUrl = `com.google.calendar://event?action=create&text=${encodeURIComponent(
        title
      )}&details=${encodeURIComponent(`${truncatedDetails}\n${process.env.PUBLIC_URL}`)}&location=${encodeURIComponent(
        location
      )}`;
      if (start && end) {
        if (isAllDay) {
          googleCalendarUrl += `&dates=${formatDateForGoogle(start)}/${formatDateForGoogle(end)}`;
        }
      }
      await AppLauncher.openUrl({ url: googleCalendarUrl });
      result = true;
    } else {
      result = await addEventToCalendarPromptHelper({
        title,
        startDate: start,
        endDate: end,
        location,
        isAllDay,
        url: process.env.PUBLIC_URL,
        description: details
      });
    }
  } else {
    var googleCalendarUrl = `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(
      title
    )}&details=${encodeURIComponent(`${truncatedDetails}\n${process.env.PUBLIC_URL}`)}&location=${encodeURIComponent(
      location
    )}&sf=true&output=xml`;
    if (start && end) {
      if (isAllDay) {
        googleCalendarUrl += `&dates=${formatDateForGoogle(start)}/${formatDateForGoogle(end)}`;
      }
    }
    window.open(googleCalendarUrl, '_blank');
    result = true;
  }
  return result;
}

export { addEventToCalendarPrompt, checkAndRecoverCalendarEvent };
